import {
  Anchor,
  Button,
  Group,
  Input,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { IconRefresh, IconSearch } from '@tabler/icons-react'
import { sortBy } from 'lodash-es'
import { ReactNode, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useGetDeviceDetails } from '@/queries/deviceQueries'
import { RouterPath } from '@/router/paths'
import { MilestoneCamera } from '@/types/vlm'
import { getAugmentedCameras } from '@/utils/device/getAugmentedCameras'
import { MilestoneCameraList, MilestoneCameraRow } from './MilestoneCameraList'
import { validateMaxCameras } from './validateMaxCameras'

type RegisteredCameraSelectionProps = {
  deviceId: string
  value: MilestoneCamera[]
  onChange: (cameras: MilestoneCamera[]) => void
  error?: ReactNode
  description?: ReactNode
}

const matchesSearch = (row: MilestoneCameraRow, search: string) =>
  row.name.toLowerCase().includes(search.trim().toLowerCase())

export const RegisteredCameraSelection = ({
  deviceId,
  value,
  onChange,
  error: selectionError,
  description
}: RegisteredCameraSelectionProps) => {
  const intl = useIntl()
  const [search, setSearch] = useState('')

  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetDeviceDetails(deviceId)

  const cameras = data
    ? sortBy(
        getAugmentedCameras(data).filter((c) => c.stream_type === 'rtsp'),
        (c) => c.camera_name.toLowerCase()
      )
    : []
  const cameraIds = new Set(cameras.map((c) => c.camera_id))
  const selectedIds = new Set(value.map((c) => c.camera_id))
  const missing = data ? value.filter((c) => !cameraIds.has(c.camera_id)) : []

  const rows: MilestoneCameraRow[] = [
    ...cameras.map((c) => ({
      camera_id: c.camera_id,
      name: c.camera_name,
      enabled: true,
      missing: false,
      offline: !c.connected,
      hideId: true
    })),
    ...missing.map((c) => ({
      ...c,
      enabled: true,
      missing: true,
      hideId: true
    }))
  ]
  const visibleRows = rows.filter((row) => matchesSearch(row, search))
  const hasOfflineSelected = rows.some(
    (row) => row.offline && selectedIds.has(row.camera_id)
  )

  const setSelection = (ids: Set<string>) => {
    onChange(
      rows
        .filter((row) => ids.has(row.camera_id))
        .map(({ camera_id, name }) => ({ camera_id, name }))
    )
  }

  const handleToggle = (row: MilestoneCameraRow) => {
    const ids = new Set(selectedIds)
    if (ids.has(row.camera_id)) ids.delete(row.camera_id)
    else ids.add(row.camera_id)
    setSelection(ids)
  }

  const handleToggleAll = (targets: MilestoneCameraRow[], select: boolean) => {
    const ids = new Set(selectedIds)
    targets.forEach((row) =>
      select ? ids.add(row.camera_id) : ids.delete(row.camera_id)
    )
    setSelection(ids)
  }

  const renderCameras = () => {
    if (isLoading)
      return (
        <Stack align="center" gap="xs" py="xl">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            <FormattedMessage id="vlm.pipelines.create.cameras.registered.loading" />
          </Text>
        </Stack>
      )

    if (isError)
      return (
        <PlatformAlert variant="warning">
          <Stack gap="xs" align="flex-start">
            <Text size="sm">
              {extractErrorMessage(
                error,
                intl.formatMessage({
                  id: 'vlm.pipelines.create.cameras.registered.error'
                }),
                { includeFieldNameInValidationErrors: false }
              )}
            </Text>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={() => void refetch()}
            >
              <FormattedMessage id="vlm.pipelines.create.cameras.retry" />
            </Button>
          </Stack>
        </PlatformAlert>
      )

    if (rows.length === 0)
      return (
        <Stack align="center" gap={4} py="lg">
          <Text size="sm" c="dimmed">
            <FormattedMessage id="vlm.pipelines.create.cameras.registered.empty" />
          </Text>
          <Anchor component={Link} to={RouterPath.cameras} size="sm">
            <FormattedMessage id="vlm.pipelines.create.cameras.registered.emptyLink" />
          </Anchor>
        </Stack>
      )

    return (
      <Stack gap="sm">
        {hasOfflineSelected && (
          <PlatformAlert variant="warning" compact>
            <FormattedMessage id="vlm.pipelines.create.cameras.registered.offlineWarning" />
          </PlatformAlert>
        )}
        <MilestoneCameraList
          rows={visibleRows}
          selectedIds={selectedIds}
          missingLabel={
            <FormattedMessage id="vlm.pipelines.create.cameras.registered.missing" />
          }
          onToggle={handleToggle}
          onToggleAll={handleToggleAll}
        />
      </Stack>
    )
  }

  return (
    <Stack gap="md">
      <Input.Wrapper
        label={<FormattedMessage id="vlm.pipelines.create.cameras.label" />}
        description={
          <>
            {description && (
              <>
                {description}
                <br />
              </>
            )}
            <FormattedMessage
              id="vlm.pipelines.create.cameras.selected"
              values={{ count: value.length }}
            />
          </>
        }
        error={selectionError ?? validateMaxCameras(intl, value)}
      >
        <Paper my={4} p="sm" withBorder>
          <Stack gap="sm">
            <Group gap="sm" wrap="nowrap">
              <TextInput
                style={{ flex: 1 }}
                leftSection={<IconSearch size={16} />}
                placeholder={intl.formatMessage({
                  id: 'vlm.pipelines.create.cameras.registered.search'
                })}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
              />
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                loading={isFetching}
                onClick={() => void refetch()}
              >
                <FormattedMessage id="vlm.pipelines.create.cameras.reload" />
              </Button>
            </Group>

            {renderCameras()}
          </Stack>
        </Paper>
      </Input.Wrapper>
    </Stack>
  )
}
